import { useEffect, type ReactNode } from "react"
import { cn } from "@/lib/utils"

export type SidebarDrawerSide = "left" | "right"

export interface SidebarDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  side?: SidebarDrawerSide
  widthClassName?: string
  ariaLabel?: string
  lockBodyScroll?: boolean
  closeOnEscape?: boolean
  className?: string
}

export function SidebarDrawer({
  open,
  onOpenChange,
  children,
  side = "left",
  widthClassName = "w-2/3 md:w-52",
  ariaLabel = "Navigation",
  lockBodyScroll = true,
  closeOnEscape = true,
  className,
}: SidebarDrawerProps) {
  useEffect(() => {
    if (!lockBodyScroll || !open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [lockBodyScroll, open])

  useEffect(() => {
    if (!closeOnEscape || !open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [closeOnEscape, open, onOpenChange])

  const isLeft = side === "left"

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 motion-reduce:transition-none",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <div
        className={cn(
          "fixed inset-y-0 z-50 bg-background pb-[env(safe-area-inset-bottom)] [&>div]:!w-full transition-transform duration-200 ease-out motion-reduce:transition-none",
          isLeft ? "left-0" : "right-0",
          widthClassName,
          isLeft
            ? open
              ? "translate-x-0"
              : "-translate-x-full"
            : open
              ? "translate-x-0"
              : "translate-x-full",
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </>
  )
}
